import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Testimonials marquee — clone + -50% loop (announcement-bar pattern).
 * Drag/swipe freezes at the release position, then continues from there via WAAPI
 * (no CSS animation-delay seek, which was causing jumps).
 *
 * @extends {Component<{}>}
 */
export class TestimonialsMarquee extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  /** @type {HTMLDialogElement | null} */
  #dialogElement = null;

  /** @type {((event: MouseEvent) => void) | undefined} */
  #onDialogClick;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {Animation | null} */
  #marqueeAnimation = null;

  /** @type {number} */
  #loopWidth = 0;

  /** @type {number} */
  #lastViewportWidth = 0;

  /** @type {number | undefined} */
  #resizeTimer;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragOriginX = 0;

  /** @type {number} */
  #dragOffsetX = 0;

  /** @type {boolean} */
  #dragging = false;

  /** @type {boolean} */
  #dragArmed = false;

  /** @type {boolean} */
  #didDrag = false;

  /** @type {number | null} */
  #pointerId = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-disabled')) return;

    this.#setupMarquee();
    this.#resizeObserver = new ResizeObserver((entries) => {
      if (this.hasAttribute('data-testimonial-dialog-open') || this.#dragging || this.#dragArmed) return;

      const entry = entries[0];
      const nextWidth = entry?.contentRect?.width ?? this.clientWidth;
      if (Math.abs(nextWidth - this.#lastViewportWidth) < 2) return;

      window.clearTimeout(this.#resizeTimer);
      this.#resizeTimer = window.setTimeout(() => this.#setupMarquee(), 150);
    });
    this.#resizeObserver.observe(this);

    this.#dialogComponent = this.closest('section')?.querySelector('dialog-component[data-testimonial-dialog]') ?? null;
    this.#dialogElement = this.#dialogComponent?.querySelector('dialog') ?? null;
    this.#dialogComponent?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#onDialogClick = (event) => this.#handleDialogBackdropClick(event);
    this.#dialogElement?.addEventListener('click', this.#onDialogClick);

    this.addEventListener('click', this.#handleCardClick);
    this.addEventListener('pointerdown', this.#handlePointerDown);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    window.clearTimeout(this.#resizeTimer);
    this.#cancelMarqueeAnimation();
    this.#dialogComponent?.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    if (this.#onDialogClick) {
      this.#dialogElement?.removeEventListener('click', this.#onDialogClick);
    }
    this.removeEventListener('click', this.#handleCardClick);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#unbindDragListeners();
  }

  #handleDialogClose = () => {
    this.removeAttribute('data-testimonial-dialog-open');
    if (this.#marqueeAnimation && this.#marqueeAnimation.playState === 'paused') {
      this.#marqueeAnimation.play();
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDialogBackdropClick(event) {
    if (!(this.#dialogElement instanceof HTMLDialogElement) || !this.#dialogElement.open) return;

    const shell = this.#dialogElement.querySelector('.om-testimonial-modal__shell');
    if (!(shell instanceof HTMLElement)) return;
    if (event.target instanceof Node && shell.contains(event.target)) return;

    if (this.#dialogComponent && 'closeDialog' in this.#dialogComponent) {
      /** @type {{ closeDialog: () => void }} */ (this.#dialogComponent).closeDialog();
    }
  }

  #handleCardClick = (event) => {
    if (this.hasAttribute('data-disabled')) return;

    if (this.#didDrag) {
      event.preventDefault();
      event.stopPropagation();
      this.#didDrag = false;
      return;
    }

    const trigger = event.target instanceof Element ? event.target.closest('.om-testimonial-card__trigger') : null;
    if (!trigger) return;

    const card = trigger.closest('[data-testimonial-item]');
    if (!(card instanceof HTMLElement)) return;
    if (card.hasAttribute('data-testimonial-fill') || card.closest('[data-testimonials-clone]')) return;

    const section = this.closest('section');
    const dialogComponent = section?.querySelector('dialog-component[data-testimonial-dialog]');
    const template = card.querySelector('template[data-testimonial-detail]');
    const content = section?.querySelector('[data-testimonial-dialog-content]');
    const title = section?.querySelector('[data-testimonial-dialog-title]');

    if (
      !(dialogComponent instanceof HTMLElement && 'showDialog' in dialogComponent) ||
      !(template instanceof HTMLTemplateElement) ||
      !(content instanceof HTMLElement)
    ) {
      return;
    }

    content.replaceChildren(template.content.cloneNode(true));

    const author = content.querySelector('[data-testimonial-modal-author]');
    if (title instanceof HTMLElement) {
      title.textContent = author?.textContent?.trim() || title.dataset.defaultTitle || title.textContent;
    }

    trigger.blur();
    this.setAttribute('data-testimonial-dialog-open', '');
    this.#marqueeAnimation?.pause();
    /** @type {{ showDialog: () => void }} */ (dialogComponent).showDialog();
  };

  /**
   * @param {HTMLElement} items
   */
  #getItemsSetWidth(items) {
    return items.getBoundingClientRect().width;
  }

  /**
   * @param {HTMLElement} track
   */
  #getTranslateX(track) {
    const transform = getComputedStyle(track).transform;
    if (!transform || transform === 'none') return 0;
    try {
      return new DOMMatrixReadOnly(transform).m41;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Normalize offset into [-loop, 0] so duplicated sets stay seamless.
   *
   * @param {number} value
   */
  #wrapOffset(value) {
    const loop = this.#loopWidth;
    if (loop <= 0) return value;
    let next = value % loop;
    // JS % can be negative; keep result in (-loop, 0]
    if (next > 0) next -= loop;
    if (next === 0 && value < 0) next = 0;
    return next;
  }

  #cancelMarqueeAnimation() {
    this.#marqueeAnimation?.cancel();
    this.#marqueeAnimation = null;
  }

  /**
   * Continue infinite scroll from an exact pixel offset (no jump).
   *
   * @param {HTMLElement} track
   * @param {number} offsetX
   */
  #continueFromOffset(track, offsetX) {
    const loop = this.#loopWidth;
    if (loop <= 0) return;

    const x = this.#wrapOffset(offsetX);
    const pixelsPerSecond = Math.max(15, Number(this.dataset.speed) || 35);
    const durationMs = Math.max(12000, (loop / pixelsPerSecond) * 1000);

    this.#cancelMarqueeAnimation();
    // Disable CSS keyframes — WAAPI owns motion after a manual swipe.
    track.style.animation = 'none';
    track.style.animationDelay = '';
    track.style.animationPlayState = '';
    track.style.transform = `translate3d(${x}px, 0, 0)`;

    this.#marqueeAnimation = track.animate(
      [
        { transform: `translate3d(${x}px, 0, 0)` },
        { transform: `translate3d(${x - loop}px, 0, 0)` },
      ],
      {
        duration: durationMs,
        iterations: Infinity,
        easing: 'linear',
      }
    );
  }

  #bindDragListeners() {
    window.addEventListener('pointermove', this.#handlePointerMove, { passive: false });
    window.addEventListener('pointerup', this.#handlePointerUp);
    window.addEventListener('pointercancel', this.#handlePointerUp);
  }

  #unbindDragListeners() {
    window.removeEventListener('pointermove', this.#handlePointerMove);
    window.removeEventListener('pointerup', this.#handlePointerUp);
    window.removeEventListener('pointercancel', this.#handlePointerUp);
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (this.hasAttribute('data-disabled')) return;
    if (this.hasAttribute('data-testimonial-dialog-open')) return;
    if (event.button != null && event.button !== 0) return;
    if (!(event.target instanceof Element)) return;

    const track = this.querySelector('[data-testimonials-track]');
    if (!(track instanceof HTMLElement) || !track.hasAttribute('data-marquee-ready')) return;

    this.#track = track;
    this.#dragArmed = true;
    this.#dragging = false;
    this.#didDrag = false;
    this.#pointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragOriginX = this.#getTranslateX(track);
    this.#dragOffsetX = this.#dragOriginX;

    this.#bindDragListeners();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if ((!this.#dragArmed && !this.#dragging) || this.#pointerId !== event.pointerId || !this.#track) return;

    const delta = event.clientX - this.#dragStartX;

    if (!this.#dragging) {
      if (Math.abs(delta) < 8) return;

      this.#dragging = true;
      this.#didDrag = true;
      this.#dragOriginX = this.#getTranslateX(this.#track);
      this.#dragOffsetX = this.#dragOriginX;
      this.#dragStartX = event.clientX;

      // Freeze at the live pixel position — cancel CSS / WAAPI motion.
      this.#cancelMarqueeAnimation();
      this.#track.style.animation = 'none';
      this.#track.style.animationDelay = '';
      this.#track.style.animationPlayState = '';
      this.#track.style.transform = `translate3d(${this.#dragOffsetX}px, 0, 0)`;
      this.setAttribute('data-dragging', '');

      try {
        this.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture unsupported */
      }
    }

    // Do not wrap while dragging — keeps the finger-locked position exact.
    const moveDelta = event.clientX - this.#dragStartX;
    this.#dragOffsetX = this.#dragOriginX + moveDelta;
    this.#track.style.transform = `translate3d(${this.#dragOffsetX}px, 0, 0)`;
    event.preventDefault();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (this.#pointerId != null && event.pointerId !== this.#pointerId) return;

    const track = this.#track;
    const wasDragging = this.#dragging;
    const offset = this.#dragOffsetX;

    this.#dragging = false;
    this.#dragArmed = false;
    this.#pointerId = null;
    this.#track = null;
    this.removeAttribute('data-dragging');
    this.#unbindDragListeners();

    if (!(track instanceof HTMLElement) || !wasDragging) return;

    // Stay exactly where the finger left off, then keep scrolling from there.
    this.#continueFromOffset(track, offset);
  };

  #setupMarquee() {
    const track = this.querySelector('[data-testimonials-track]');
    const items = this.querySelector('[data-testimonials-items]');
    if (!(track instanceof HTMLElement) || !(items instanceof HTMLElement)) return;

    this.#cancelMarqueeAnimation();
    track.querySelectorAll('[data-testimonials-clone]').forEach((node) => node.remove());
    items.querySelectorAll('[data-testimonial-fill]').forEach((node) => node.remove());
    track.removeAttribute('data-marquee-ready');
    track.style.animation = '';
    track.style.animationDelay = '';
    track.style.transform = '';
    track.style.animationPlayState = '';

    const sourceItems = [...items.querySelectorAll('[data-testimonial-item]')];
    if (sourceItems.length === 0) return;

    const viewportEl = this.querySelector('[data-testimonials-marquee]');
    const viewportWidth = viewportEl instanceof HTMLElement ? viewportEl.clientWidth : this.clientWidth;
    this.#lastViewportWidth = this.clientWidth;

    while (this.#getItemsSetWidth(items) < viewportWidth && items.children.length < 40) {
      for (const item of sourceItems) {
        const fill = /** @type {HTMLElement} */ (item.cloneNode(true));
        fill.removeAttribute('data-shopify-editor-block');
        fill.removeAttribute('id');
        fill.setAttribute('data-testimonial-fill', '');
        fill.setAttribute('aria-hidden', 'true');
        items.appendChild(fill);
        if (this.#getItemsSetWidth(items) >= viewportWidth) break;
      }
    }

    const clone = /** @type {HTMLElement} */ (items.cloneNode(true));
    clone.setAttribute('data-testimonials-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('role');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
      el.removeAttribute('data-shopify-editor-block');
    });
    clone.querySelectorAll('[id^="shopify-block-"]').forEach((el) => {
      el.removeAttribute('id');
    });
    clone.querySelectorAll('[data-testimonial-item]').forEach((el) => {
      this.#makeInert(/** @type {HTMLElement} */ (el));
    });
    track.appendChild(clone);

    const itemsSetWidth = this.#getItemsSetWidth(items);
    this.#loopWidth = itemsSetWidth;

    const pixelsPerSecond = Math.max(15, Number(this.dataset.speed) || 35);
    const duration = Math.max(12, itemsSetWidth / pixelsPerSecond);

    this.style.setProperty('--testimonials-marquee-duration', `${duration.toFixed(2)}s`);
    track.setAttribute('data-marquee-ready', 'true');
  }

  /**
   * @param {HTMLElement} root
   */
  #makeInert(root) {
    try {
      root.inert = true;
    } catch (error) {
      /* inert unsupported */
    }

    root.setAttribute('tabindex', '-1');
    root.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
      el.setAttribute('tabindex', '-1');
      if (el.hasAttribute('href')) {
        el.removeAttribute('href');
      }
    });
  }
}

if (!customElements.get('testimonials-marquee-component')) {
  customElements.define('testimonials-marquee-component', TestimonialsMarquee);
}
